'use client'

import { useEffect, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { Menu, X } from 'lucide-react'
import { CHROME_DOT } from '@/components/ui/chromeDot'
import UpgradeButton from '@/components/upgrade/UpgradeButton'
import { useUpgradeModal } from '@/components/upgrade/UpgradeModalProvider'
import { HEADER_NAV_LINKS } from '@/lib/siteNav'

// The phone and tablet menu: the header's links, Pricing and the account, in
// a floating panel under the pill. Rows are set large — a menu on a phone is
// read at arm's length and tapped with a thumb.
//
// Portalled to <body>. The header pill is glass (`backdrop-filter`), and a
// filtered element is the containing block for its `position: fixed`
// descendants — rendered in place, the "full-screen" scrim and the panel
// would be clipped to the pill itself.

const ROW =
  'flex min-h-12 w-full items-center rounded-xl px-3 text-left text-heading font-semibold text-[var(--rt-ink)] '
  + 'transition-colors hover:bg-[var(--rt-hover)]'

const subscribeNever = () => () => {}

export default function LandingNavMenu({ signedIn, unlimited = false }: { signedIn: boolean; unlimited?: boolean }) {
  const [open, setOpen] = useState(false)
  const plans = useUpgradeModal()
  // A portal needs <body>, which the server render does not have.
  const mounted = useSyncExternalStore(subscribeNever, () => true, () => false)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open])

  const close = () => setOpen(false)

  return (
    <div className="lg:hidden">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="landing-nav-drawer"
        aria-label={open ? 'Close menu' : 'Open menu'}
        onClick={() => setOpen((v) => !v)}
        className={CHROME_DOT}
      >
        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {open && mounted && createPortal(
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="upgrade-backdrop fixed inset-0 z-40 bg-[color-mix(in_srgb,var(--rt-shadow)_30%,transparent)] backdrop-blur-[2px]"
            onClick={close}
          />
          <nav
            id="landing-nav-drawer"
            aria-label="Site"
            className="rt-glass rt-rise-in fixed inset-x-3 top-[4.5rem] z-50 rounded-2xl p-2 sm:inset-x-4"
          >
            <ul className="flex flex-col">
              {HEADER_NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <Link href={l.href} className={ROW} onClick={close}>
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <UpgradeButton
                  isPro={unlimited}
                  appearance="menu"
                  className={ROW}
                  onOpen={() => { close(); plans.open({ trigger: 'header_upgrade' }) }}
                />
              </li>
              <li className="mt-1 border-t border-[var(--app-border)] pt-1">
                <Link href={signedIn ? '/rooms' : '/login'} className={ROW} onClick={close}>
                  {signedIn ? 'My rooms' : 'Sign in'}
                </Link>
              </li>
            </ul>
          </nav>
        </>,
        document.body,
      )}
    </div>
  )
}
